import { Link, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { track } from "@/lib/analytics";
import { GENERIC_BOKADIREKT } from "@/lib/booking";
import { Logo } from "@/components/Logo";
import { primaryNav, footerSalonSlugs } from "@/data/navigation";
import { salons } from "@/data/salons";
import { internalPath } from "@/lib/paths";

const navItems = primaryNav;
const headerSalons = footerSalonSlugs
  .map((slug) => salons.find((s) => s.slug === slug))
  .filter((s): s is (typeof salons)[number] => Boolean(s));

const headerServices: { label: string; to: string }[] = [
  { label: "Herrklippning", to: "/herrklippning" },
  { label: "Skäggtrimning", to: "/skaggtrimning" },
  { label: "Hår & skägg", to: "/herrklippning-skaggtrimning" },
  { label: "Taper fade", to: "/taper-fade" },
  { label: "Skin fade", to: "/skin-fade" },
  { label: "Barnklippning", to: "/barnklippning-stockholm" },
  { label: "Seniorklippning", to: "/seniorklippning-pensionarsklippning" },
  { label: "Herrfrisyrer", to: "/herrfrisyrer-2026" },
];

const headerInspiration: { label: string; to: string }[] = [
  { label: "Blogg", to: "/blogg" },
];

function LangSwitch({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isEn = pathname.startsWith("/en");
  const base =
    "px-2 py-1 rounded text-xs font-medium uppercase tracking-wide transition-colors";
  return (
    <div className="inline-flex items-center gap-0.5 border border-border rounded-md p-0.5" aria-label="Språk">
      <Link
        to="/"
        onClick={onNavigate}
        hrefLang="sv"
        aria-current={isEn ? undefined : "true"}
        className={`${base} ${isEn ? "text-foreground/60 hover:text-foreground" : "bg-muted text-foreground"}`}
      >
        SV
      </Link>
      <Link
        to="/en/"
        onClick={onNavigate}
        hrefLang="en"
        aria-current={isEn ? "true" : undefined}
        className={`${base} ${isEn ? "bg-muted text-foreground" : "text-foreground/60 hover:text-foreground"}`}
      >
        EN
      </Link>
    </div>
  );
}

export function Header({ bookingUrl = GENERIC_BOKADIREKT }: { bookingUrl?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-md border-b border-border">
      <div className="container-page flex items-center justify-between h-16 md:h-20">
        <Logo size={44} />
        <nav className="hidden md:flex items-center gap-8 text-sm">
          {navItems.map((n) => {
            if (n.label === "Salonger") {
              return (
                <div key={n.to} className="relative group">
                  <Link
                    to={internalPath(n.to)}
                    className="inline-flex items-center gap-1 text-foreground/80 hover:text-foreground transition-colors py-2"
                  >
                    {n.label}
                    <ChevronDown size={14} className="opacity-70" />
                  </Link>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-opacity">
                    <div className="min-w-[220px] rounded-lg border border-border bg-background shadow-lg py-2">
                      {headerSalons.map((s) => (
                        <Link
                          key={s.slug}
                          to={internalPath(s.url)}
                          className="block px-4 py-2 text-sm text-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
                        >
                          {s.shortName}
                        </Link>
                      ))}
                      <div className="my-1 border-t border-border" />
                      <Link
                        to="/"
                        className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                      >
                        Alla salonger
                      </Link>
                    </div>
                  </div>
                </div>
              );
            }
            if (n.label === "Tjänster") {
              return (
                <div key={n.to} className="relative group">
                  <Link
                    to={internalPath(n.to)}
                    className="inline-flex items-center gap-1 text-foreground/80 hover:text-foreground transition-colors py-2"
                  >
                    {n.label}
                    <ChevronDown size={14} className="opacity-70" />
                  </Link>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-opacity">
                    <div className="min-w-[240px] rounded-lg border border-border bg-background shadow-lg py-2">
                      {headerServices.map((s) => (
                        <Link
                          key={s.to}
                          to={internalPath(s.to)}
                          className="block px-4 py-2 text-sm text-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
                        >
                          {s.label}
                        </Link>
                      ))}
                      <div className="my-1 border-t border-border" />
                      <Link
                        to="/priser/"
                        className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                      >
                        Se alla priser
                      </Link>
                    </div>
                  </div>
                </div>
              );
            }
            if (n.label === "Inspiration") {
              return (
                <div key={n.to} className="relative group">
                  <Link
                    to={internalPath(n.to)}
                    className="inline-flex items-center gap-1 text-foreground/80 hover:text-foreground transition-colors py-2"
                  >
                    {n.label}
                    <ChevronDown size={14} className="opacity-70" />
                  </Link>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-opacity">
                    <div className="min-w-[240px] rounded-lg border border-border bg-background shadow-lg py-2">
                      {headerInspiration.map((s) => (
                        <Link
                          key={s.to}
                          to={internalPath(s.to)}
                          className="block px-4 py-2 text-sm text-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={internalPath(n.to)}
                className="text-foreground/80 hover:text-foreground transition-colors"
              >
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-2">
          <LangSwitch />
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-cta="header"
            onClick={() => track("book_now_click", { cta_location: "header" })}
            className="hidden md:inline-flex btn-primary"
          >
            Boka tid
          </a>
          <button
            type="button"
            aria-label="Meny"
            className="md:hidden p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-background">
          <nav className="container-page py-4 flex flex-col gap-1">
            {navItems.map((n) => {
              if (n.label === "Salonger") {
                return (
                  <div key={n.to} className="flex flex-col">
                    <span className="py-3 text-base text-foreground/80">Salonger</span>
                    <div className="pl-4 flex flex-col border-l border-border ml-2">
                      {headerSalons.map((s) => (
                        <Link
                          key={s.slug}
                          to={internalPath(s.url)}
                          onClick={() => setOpen(false)}
                          className="py-2 text-base text-foreground/70 hover:text-foreground"
                        >
                          {s.shortName}
                        </Link>
                      ))}
                      <Link
                        to="/"
                        onClick={() => setOpen(false)}
                        className="py-2 text-base font-medium text-foreground"
                      >
                        Alla salonger
                      </Link>
                    </div>
                  </div>
                );
              }
              if (n.label === "Tjänster") {
                return (
                  <div key={n.to} className="flex flex-col">
                    <span className="py-3 text-base text-foreground/80">Tjänster</span>
                    <div className="pl-4 flex flex-col border-l border-border ml-2">
                      {headerServices.map((s) => (
                        <Link
                          key={s.to}
                          to={internalPath(s.to)}
                          onClick={() => setOpen(false)}
                          className="py-2 text-base text-foreground/70 hover:text-foreground"
                        >
                          {s.label}
                        </Link>
                      ))}
                      <Link
                        to="/priser/"
                        onClick={() => setOpen(false)}
                        className="py-2 text-base font-medium text-foreground"
                      >
                        Se alla priser
                      </Link>
                    </div>
                  </div>
                );
              }
              if (n.label === "Inspiration") {
                return (
                  <div key={n.to} className="flex flex-col">
                    <span className="py-3 text-base text-foreground/80">Inspiration</span>
                    <div className="pl-4 flex flex-col border-l border-border ml-2">
                      {headerInspiration.map((s) => (
                        <Link
                          key={s.to}
                          to={internalPath(s.to)}
                          onClick={() => setOpen(false)}
                          className="py-2 text-base text-foreground/70 hover:text-foreground"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={n.to}
                  to={internalPath(n.to)}
                  onClick={() => setOpen(false)}
                  className="py-3 text-base"
                >
                  {n.label}
                </Link>
              );
            })}
            <div className="py-3">
              <LangSwitch onNavigate={() => setOpen(false)} />
            </div>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-cta="mobile_menu"
              className="btn-primary mt-2"
              onClick={() => {
                setOpen(false);
                track("book_now_click", { cta_location: "mobile_menu" });
              }}
            >
              Boka tid
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
